import { useCallback, useEffect, useState } from 'react';
import { auditsAPI, type AuditTemplate } from '../../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from './useAuditActions';

interface AuditTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Choose which questionnaire templates are completed by your own auditors.
 * Marking one affects new audits only; questionnaires already sent to
 * suppliers stay with them. A template with audits stays an audit template.
 */
export default function AuditTemplatesDialog({ open, onOpenChange }: AuditTemplatesDialogProps) {
  const [templates, setTemplates] = useState<AuditTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    auditsAPI.templates()
      .then(setTemplates)
      .catch((err: unknown) => {
        console.error('Failed to load audit templates:', err);
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (template: AuditTemplate, isAudit: boolean) => {
    setBusy(template.id);
    try {
      const updated = await auditsAPI.setTemplateAudit(template.id, isAudit);
      setTemplates((rows) => rows.map((t) => (t.id === updated.id ? updated : t)));
      toast.success('Audit templates updated');
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not update the template.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Audit templates</DialogTitle>
          <DialogDescription>
            Templates switched on here are completed by your own auditors, from Audits. Suppliers never see audit
            templates or audits. Questionnaires already sent to suppliers aren&apos;t changed.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading templates…
          </div>
        ) : loadFailed ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm">Could not load your templates.</p>
            <Button variant="outline" onClick={load}>Retry</Button>
          </div>
        ) : templates.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No active templates yet.</p>
        ) : (
          <ul className="divide-y max-h-[60vh] overflow-y-auto">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <p className="font-medium text-base">{t.name}</p>
                  {t.is_audit && t.supplier_assignments > 0 && (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t.supplier_assignments === 1
                        ? '1 questionnaire from this template is still with a supplier and stays with them.'
                        : `${t.supplier_assignments} questionnaires from this template are still with suppliers and stay with them.`}
                    </p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                  <Switch checked={t.is_audit} disabled={busy === t.id}
                    onCheckedChange={(checked) => void toggle(t, checked)}
                    aria-label={`Completed by our auditor: ${t.name}`} />
                  Completed by our auditor
                </label>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
